import { useState } from 'react'
import { Check, Plus, X } from 'lucide-react'
import type { SourcingCategory } from '@/api/sourcing'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * The store categories a backup source is used for (USA-1506): chosen categories as removable
 * chips, and a searchable list of the manufacturer's categories to add. A category covers the
 * ones below it.
 */
export function SourcingCategoryPicker({
  categories,
  value,
  onChange,
  disabled = false,
  className,
}: {
  categories: readonly SourcingCategory[]
  value: readonly number[]
  onChange: (taxonomyNodeIds: number[]) => void
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const byId = new Map(categories.map((category) => [category.taxonomy_node_id, category]))
  const chosen = value.flatMap((id) => {
    const category = byId.get(id)
    return category ? [category] : []
  })
  const query = search.trim().toLowerCase()
  const listed = query
    ? categories.filter((category) => category.path.join(' ').toLowerCase().includes(query))
    : categories
  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter((other) => other !== id) : [...value, id])

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {chosen.map((category) => (
        <span
          key={category.taxonomy_node_id}
          title={category.path.join(' › ')}
          className="inline-flex max-w-full items-center gap-1 rounded-md border bg-muted/50 py-0.5 pl-2 pr-1 text-sm"
        >
          <span className="truncate">{category.path[category.path.length - 1]}</span>
          <button
            type="button"
            onClick={() => toggle(category.taxonomy_node_id)}
            disabled={disabled}
            aria-label={`Remove ${category.path[category.path.length - 1]}`}
            className="rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <X className="size-3.5" />
          </button>
        </span>
      ))}
      <PickerMenu
        open={open}
        onOpenChange={(nextOpen) => {
          if (!disabled) setOpen(nextOpen)
          if (!nextOpen) setSearch('')
        }}
      >
        <PickerMenuTrigger asChild disabled={disabled}>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground">
            <Plus className="size-3.5" /> {chosen.length ? 'Add category' : 'Choose categories'}
          </Button>
        </PickerMenuTrigger>
        <PickerMenuContent title="Store categories" className="w-[22rem] max-w-[calc(100vw-2rem)]">
          <PickerMenuSearch
            value={search}
            onChange={setSearch}
            placeholder="Search categories"
            ariaLabel="Search categories"
          />
          <PickerMenuList className="max-h-72">
            {listed.length === 0 && (
              <p className="px-2 py-3 text-sm text-muted-foreground">No matching categories</p>
            )}
            {listed.map((category) => {
              const selected = value.includes(category.taxonomy_node_id)
              return (
                <PickerMenuItem
                  key={category.taxonomy_node_id}
                  className="min-h-9 justify-between gap-3"
                  onSelect={(event) => {
                    event.preventDefault()
                    toggle(category.taxonomy_node_id)
                  }}
                >
                  <span
                    className="min-w-0 truncate"
                    style={query ? undefined : { paddingLeft: `${(category.path.length - 1) * 0.75}rem` }}
                    title={category.path.join(' › ')}
                  >
                    {query ? category.path.join(' › ') : category.path[category.path.length - 1]}
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    {category.products}
                    {selected ? <Check className="size-4 text-foreground" /> : <span className="size-4" />}
                  </span>
                </PickerMenuItem>
              )
            })}
          </PickerMenuList>
        </PickerMenuContent>
      </PickerMenu>
    </div>
  )
}
